'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import type { Collaborator, DisableInput } from '@/app/models/collaborator';
import { useDisableAllMutation, useSearchPersonQuery } from '@/app/services/personApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import GenericAutoComplete from '@/components/common/GenericAutoComplete';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import TextButton from '@/components/common/TextButton';
import { Box, Typography, useTheme } from '@mui/material';
import NewConsiderCollaboratorTable from './NewConsiderCollaboratorTable';

interface NewConsiderCollaboratorProps {
  setDrawerOpen: (open: boolean) => void;
}

export default function NewConsiderCollaborator(props: NewConsiderCollaboratorProps) {
  const theme = useTheme();
  const { t } = useTranslation(['considerCollaborator', 'common']);

  const { setDrawerOpen } = props;

  const [selectedCollaborators, setSelectedCollaborators] = useState<Collaborator[]>([]);

  const [collaborator, setCollaborator] = useState<Collaborator | null>(null);
  const [collaboratorSearch, setCollaboratorSearch] = useState('');
  const { data: collaboratorsAutoComplete } = useSearchPersonQuery(
    {
      searchTerm: collaboratorSearch,
      available: true,
      page: 0,
      size: 10,
      sortKey: 'name',
      sortDirection: 'ASC',
    },
    { skip: collaboratorSearch.length < 1 },
  );

  const handleAddCollaborator = () => {
    if (collaborator) {
      setSelectedCollaborators((prevValue) => [...prevValue, collaborator]);
      setCollaborator(null);
      setCollaboratorSearch('');
    }
  };

  const handleRemove = (removedCollaborator: number) => {
    setSelectedCollaborators((prevValue) => prevValue.filter((col) => col.id !== removedCollaborator));
  };

  const [disableAll] = useDisableAllMutation();

  const handleSubmit = async () => {
    if (!selectedCollaborators || selectedCollaborators.length < 1) return;
    toast.info(t('new.value.sent'));
    const requestBody: DisableInput = {
      ids: selectedCollaborators.map((i) => i.id),
    };

    await disableAll(requestBody);
    setDrawerOpen(false);
    toast.success(t('new.value.added'));
  };

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const alreadyIncluded = (value: Collaborator) => {
    const filteredCollaborators = selectedCollaborators.filter((item) => item.id === value.id);
    return filteredCollaborators.length >= 1;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('desconsider.from.sharing')} subtitle={t('desconsider.subtitle') as string} />

        <Box sx={{ mt: 4 }}>
          <Typography component="p" variant="fieldHeader" sx={{ mt: 2 }}>
            {t('collaborator')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <GenericAutoComplete
            value={collaborator}
            onChange={(value) => {
              setCollaborator(value);
            }}
            equalityTest={(option, value) => option?.code === value?.code}
            getOptionLabel={(option) => `${option?.badge} - ${option?.name}` || ''}
            onSearch={(value) => {
              setCollaboratorSearch(value);
            }}
            options={
              collaboratorSearch !== '' && collaboratorsAutoComplete?.content ? collaboratorsAutoComplete?.content : []
            }
            placeholder={t('search.collaborator') as string}
            sx={{ width: theme.spacing(50) }}
          />

          <TextButton
            text={`+ ${t('remove')}`}
            disabled={!collaborator || alreadyIncluded(collaborator)}
            onClick={handleAddCollaborator}
          />
        </Box>

        <Box sx={{ mt: 4 }}>
          <NewConsiderCollaboratorTable collaborators={selectedCollaborators} onRemove={handleRemove} />
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleSubmit} onCancel={handleCancel} disabled={selectedCollaborators.length < 1} />
      </Box>
    </Box>
  );
}
